import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ApiService } from 'src/app/services/api.service';
import { AssessmentRow } from 'src/app/view-models/lms.models';

@Component({
  selector: 'app-assessments',
  templateUrl: './assessments.component.html'
})
export class AssessmentsComponent implements OnInit {
  assessments: AssessmentRow[] = [];
  clearedCount: number = 0;
  totalAssessments: number = 0;
  completedCount: number = 0;
  isLoading: boolean = true;

  // Feedback State
  overallRating: number = 0;
  overallComment: string = '';
  showCommentModal: boolean = false;
  isSubmittingFeedback: boolean = false;

  constructor(private apiService: ApiService, private router: Router) {}

  ngOnInit(): void {
    this.loadAssessments();
    this.loadFeedback();
  }

  loadAssessments(): void {
    this.apiService.getAssessments().subscribe({
      next: (res) => {
        this.assessments = res.assessments.sort((a: any, b: any) => a.order - b.order);
        this.clearedCount = res.cleared_count;
        this.completedCount = res.completed_count || 0; // Map the new field here
        this.totalAssessments = res.total_assessments;
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
      }
    });
  }

  loadFeedback(): void {
    this.apiService.getTrainingFeedback().subscribe({
      next: (res) => {
        if (res && res.overall) {
          this.overallRating = res.overall.rating || 0;
          this.overallComment = res.overall.comment || '';
        }
      },
      error: () => {
        // Silently fail if feedback cannot be loaded
      }
    });
  }

  setRating(rating: number): void {
    this.overallRating = rating;
    this.submitFeedback();
  }

  openCommentModal(): void {
    // Only allow commenting if a rating has been selected first
    if (this.overallRating > 0) {
      this.showCommentModal = true;
    }
  }

  submitFeedback(): void {
    if (this.overallRating === 0) return;
    this.isSubmittingFeedback = true;

    // Omitting assessment_key updates the overall induction training feedback
    const payload = {
      rating: this.overallRating,
      comment: this.overallComment
    };

    this.apiService.submitTrainingFeedback(payload).subscribe({
      next: () => {
        this.isSubmittingFeedback = false;
        this.showCommentModal = false;
      },
      error: () => {
        this.isSubmittingFeedback = false;
        this.showCommentModal = false;
      }
    });
  }

  getButtonText(assessment: AssessmentRow): string {
    switch (assessment.status) {
      case 'not_started': return 'Start';
      case 'in_progress': return 'Continue';
      case 'cleared': return 'View results';
      case 'not_cleared': return assessment.can_retake ? 'Start retake' : 'View results';
      default: return 'Start';
    }
  }

  getBadgeText(assessment: AssessmentRow): string {
    if (!assessment.status) return '';

    // Dynamically take the API status string, capitalize the first letter, and remove underscores
    const formattedStatus = assessment.status.charAt(0).toUpperCase() + assessment.status.slice(1).replace(/_/g, ' ');

    // Append the score only if the status is cleared
    if (assessment.status === 'cleared' && assessment.score_percent !== null) {
      return `${formattedStatus} - ${assessment.score_percent}%`;
    }

    return formattedStatus;
  }

  getBadgeClass(assessment: AssessmentRow): string {
    if (assessment.status === 'cleared') return 'bg-[#E9F0E6] text-[#1A5C3D]';
    if (assessment.status === 'not_cleared' && !assessment.can_retake) return 'bg-[#FBE9E9] text-[#D92D20]';
    if (assessment.status === 'not_cleared' && assessment.can_retake) return 'bg-[#FEF3EB] text-[#B95000]';
    return 'bg-gray-100 text-gray-500';
  }

  onAction(assessment: AssessmentRow): void {
    const btnText = this.getButtonText(assessment);
    
    if (btnText === 'Start' || btnText === 'Start retake' || btnText === 'Continue') {
      this.router.navigate(['/techsure/take', assessment.assessment_key]);
    } else {
      if (assessment.attempt_id) {
        this.router.navigate(['/techsure/results', assessment.attempt_id]);
      }
    }
  }
}